import { Link, router } from "@inertiajs/react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { DetailGrid } from "@/components/backoffice/DetailGrid"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useConfirm } from "@/components/ui/confirm-dialog"
import { DocumentUploader } from "@/components/shared/DocumentUploader"
import { CheckCircle2, CreditCard, Calendar, Video, FileCheck, ExternalLink } from "lucide-react"
import type { BackofficeNotarizationDetailed, NotarizationAct } from "@/types"

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

function formatDate(date: string): string {
  if (!date) return "\u2014"
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function formatDateTime(date: string): string {
  if (!date) return "\u2014"
  return new Date(date).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
}

function StageBadge({ stage }: { stage: string }) {
  const config: Record<string, { label: string; className: string }> = {
    application: { label: "Application", className: "bg-secondary text-secondary-foreground" },
    payment: { label: "Payment", className: "bg-yellow-600 text-white" },
    scheduling: { label: "Scheduling", className: "bg-blue-600 text-white" },
    scheduled: { label: "Scheduled", className: "bg-indigo-600 text-white" },
    in_progress: { label: "In Progress", className: "bg-purple-600 text-white" },
    completed: { label: "Completed", className: "bg-green-600 text-white" },
    cancelled: { label: "Cancelled", className: "bg-gray-600 text-white" },
  }
  const { label, className } = config[stage] || { label: stage, className: "bg-secondary text-secondary-foreground" }
  return <Badge className={`font-normal text-xs ${className}`}>{label}</Badge>
}

type Props = {
  notarization: BackofficeNotarizationDetailed
}

export default function BackofficeNotarizationsShow({ notarization }: Props) {
  const confirm = useConfirm()

  const handleInvoice = () => {
    if (notarization.payment) {
      router.visit(`/backoffice/notarizations/${notarization.id}/payments/${notarization.payment.id}/invoice`)
    }
  }

  const handleConfirmPayment = async () => {
    if (!(await confirm({ title: "Confirm Payment", description: "Mark payment as confirmed?", confirmLabel: "Confirm" }))) return
    router.post(`/backoffice/notarizations/${notarization.id}/payments/confirm`)
  }

  const handleRefund = async () => {
    if (!(await confirm({ title: "Refund Payment", description: "Refund this payment? This cannot be undone.", confirmLabel: "Refund", destructive: true }))) return
    router.post(`/backoffice/notarizations/${notarization.id}/payments/refund`)
  }

  return (
    <BackofficeLayout title={notarization.business_name} breadcrumbs={[{ label: "Backoffice", href: "/backoffice/dashboard" }, { label: "Notarizations", href: "/backoffice/notarizations" }, { label: notarization.business_name }]}>
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{notarization.business_name}</h1>
            <StageBadge stage={notarization.stage} />
          </div>
          <p className="text-sm text-muted-foreground">
            {notarization.type_name} · <span className="capitalize">{notarization.session_mode.replace(/_/g, ' ')}</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Account: {notarization.account_name} · Created {formatDate(notarization.created_at)}
          </p>
        </div>

        <DetailGrid items={[
          { label: 'Type', value: notarization.type_name },
          { label: 'Jurisdiction', value: notarization.jurisdiction_label || '\u2014' },
          { label: 'Session Mode', value: notarization.session_mode.replace(/_/g, ' ') },
          { label: 'Account', value: notarization.account_name },
          { label: 'Documents', value: `${notarization.documents_count}` },
        ]} />

        {notarization.notes && (
          <div className="py-3 border-b border-border/40">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
            <p className="text-sm">{notarization.notes}</p>
          </div>
        )}

        {/* Payment */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Payment</h2>
            <div className="flex items-center gap-2">
              {notarization.payment && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleInvoice}>
                  View Invoice
                </Button>
              )}
              {notarization.payment && !notarization.payment.paid && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleConfirmPayment}>
                  Confirm Payment
                </Button>
              )}
              {notarization.payment?.paid && (
                <>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={handleRefund}>
                    Refund
                  </Button>
                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                    <CheckCircle2 className="w-3 h-3" />
                    Paid
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="px-4 py-3">
            {notarization.payment ? (
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span>{formatCents(notarization.payment.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Government Fee</span>
                  <span>{formatCents(notarization.payment.gov_fee_cents)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span>Total</span>
                  <span>{formatCents(notarization.payment.total_cents)}</span>
                </div>
                {notarization.payment.paid && notarization.payment.paid_at && (
                  <p className="text-xs text-muted-foreground pt-0.5">Paid on {formatDate(notarization.payment.paid_at)}</p>
                )}
                {notarization.payment.invoice && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-1">
                    <span>Invoice</span>
                    <span className="font-mono">{notarization.payment.invoice.number}</span>
                  </div>
                )}
                {notarization.payment.receipt && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-0.5">
                    <span>Receipt</span>
                    <span className="font-mono">{notarization.payment.receipt.number}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-4 text-center">
                <CreditCard className="w-5 h-5 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">No payment yet</p>
              </div>
            )}
          </div>
        </section>

        {/* Appointment */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Appointment</h2>
            {notarization.appointment?.cancelled && (
              <Badge className="bg-red-100 text-red-700 font-normal text-[11px]">Cancelled</Badge>
            )}
          </div>
          <div className="px-4 py-3">
            {notarization.appointment ? (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Notary</span>
                  <Link href={`/backoffice/notaries/${notarization.appointment.notary_id}`} className="hover:underline inline-flex items-center gap-1">
                    {notarization.appointment.notary_name} <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scheduled</span>
                  <span>{formatDateTime(notarization.appointment.scheduled_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span>{notarization.appointment.duration_minutes} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Timezone</span>
                  <span>{notarization.appointment.timezone}</span>
                </div>
              </div>
            ) : (
              <div className="py-4 text-center">
                <Calendar className="w-5 h-5 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">No appointment scheduled</p>
              </div>
            )}
          </div>
        </section>

        {/* Session */}
        {notarization.session && (
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Session</h2>
            </div>
            <div className="px-4 py-3 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Provider</span>
                <span>{notarization.session.provider}</span>
              </div>
              {notarization.session.meeting_url && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Meeting URL</span>
                  <a href={notarization.session.meeting_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline inline-flex items-center gap-1 text-primary">
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {notarization.session.recording_url && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Recording</span>
                  <a href={notarization.session.recording_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline inline-flex items-center gap-1 text-primary">
                    View <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {notarization.session.started_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Started</span>
                  <span>{formatDateTime(notarization.session.started_at)}</span>
                </div>
              )}
              {notarization.session.ended_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ended</span>
                  <span>{formatDateTime(notarization.session.ended_at)}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Notarial Acts */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Notarial Acts</h2>
            <span className="text-xs text-muted-foreground">{notarization.acts.length}</span>
          </div>
          <div className="px-4">
            {notarization.acts.length > 0 ? (
              <div className="divide-y">
                {notarization.acts.map((act: NotarizationAct) => (
                  <div key={act.id} className="py-3">
                    <div className="flex items-center gap-2">
                      <FileCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium">{act.document_name}</span>
                      <Badge className="bg-secondary text-secondary-foreground font-normal text-[11px] capitalize">{act.act_type.replace(/_/g, ' ')}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 ml-5.5">
                      Seal #{act.seal_number} · Cert #{act.certificate_number} · {formatDateTime(act.notarized_at)}
                    </div>
                    {act.notes && <p className="text-xs text-muted-foreground mt-0.5 ml-5.5">{act.notes}</p>}
                    {act.certificate_url && (
                      <a href={act.certificate_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-0.5 ml-5.5 inline-flex items-center gap-1">
                        View Certificate <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <FileCheck className="w-5 h-5 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">No notarial acts recorded</p>
              </div>
            )}
          </div>
        </section>

        {/* Completion */}
        {notarization.completion && (
          <div className="py-3 border-b border-border/40">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Completion</p>
            <p className="text-sm">Completed by {notarization.completion.notary_name} on {formatDate(notarization.completion.created_at)}</p>
          </div>
        )}

        {/* Application Documents */}
        <DocumentUploader
          title="Application Documents"
          documents={notarization.application_documents || []}
          uploadPath={`/backoffice/notarizations/${notarization.id}/application_documents`}
          deletePath={(docId) => `/backoffice/notarizations/${notarization.id}/application_documents/${docId}`}
        />
      </div>
    </BackofficeLayout>
  )
}
